import { useEffect, useState } from "react";
import { Card, CardContent } from "@material-ui/core";
import { DateField } from "../components/DateField";
import LoadingData from "components/Loading/LoadingData";
import Api from "lib/api/httpRequestHelper";
import CardClientBillingOrders from "../components/CardClientBillingOrders";

const date = new Date();
const today = date.toISOString().slice(0, 10);
const firstDayOfCurrentMonth = new Date(date.getFullYear(), date.getMonth(), 1)
  .toISOString()
  .slice(0, 10);
const lastDayOfCurrentMonth = new Date(
  date.getFullYear(),
  date.getMonth() + 1,
  0
)
  .toISOString()
  .slice(0, 10);
const firstDayOfCurrentYear = new Date(date.getFullYear(), 0, 1)
  .toISOString()
  .slice(0, 10);

function LojistaSellIn({
  filters,
  structureLoading,
  setIsLoadingAll,
  handleApiError,
}) {
  const userDetails = JSON.parse(localStorage.getItem("userDetail"));
  const userDetailsVision = JSON.parse(
    localStorage.getItem("userDetailVision")
  );
  const userInfo = userDetailsVision ? userDetailsVision : userDetails;

  const ordersPageRedirectUrl = "/consulta-de-pedidos?";
  const hasAccessToOrders = userInfo?.ViewsMenu?.find(
    (item) => item.NomeInterno === "ConsultaDePedidos"
  );

  const titlesPageRedirectUrl = "/admin/relatorio-acompanhamento-titulos?";
  const hasAccessToTitles = userInfo?.ViewsMenu?.find(
    (item) => item.NomeInterno === "RelatorioAcompanhamentoTitulos"
  );

  const [monthBillingOrders, setMonthBillingOrders] = useState([]);
  const [accumulatedBillingOrders, setAccumulatedBillingOrders] = useState({
    charts: [],
    bestSellerProducts: [],
    overdueAmount: "",
    expiredTitles: "",
  });
  const [period, setPeriod] = useState({
    initialDate: firstDayOfCurrentYear,
    finalDate: today,
  });
  const [searchingByPeriod, setSearchingByPeriod] = useState(false);

  const getBillingInfo = async (initialDate, endDate, forCustomPeriod) => {
    try {
      const response = await Api.get(
        "order-sell-in-status",
        "reportApp",
        "?dashSellin=porCategoria" +
          (filters?.idLoja ? `&idLoja=${filters?.idLoja}` : "") +
          (filters?.idGrupoEconomico
            ? `&idGrupoEconomico=${filters.idGrupoEconomico}`
            : "") +
          "&dtIni=" +
          initialDate +
          "&dtFim=" +
          endDate,
        true
      );

      const billedCategories =
        response.Result.PedidosFaturados.PedidosFaturados.map((item) => ({
          title: item.Categoria,
          total: Number(
            item.TotalFaturadoCategoria.replaceAll(".", "").replace(",", ".")
          ),
          datalabel: `R$ ${item.TotalFaturadoCategoria}`,
        }));

      const billed = {
        title: "Faturas Pagas",
        total: `R$ ${response.Result.PedidosFaturados.TotalFaturado}`,
        categories: billedCategories,
        noChart: !forCustomPeriod,
      };

      const periodQueryString = `dtInit=${initialDate}&dtFim=${endDate}`;
      const expiredTitlesQueryString = "&expired=true";

      const expiredTitlesUrl =
        titlesPageRedirectUrl + periodQueryString + expiredTitlesQueryString;

      const pendingBillingCategories =
        response.Result.PedidosFaturados.PedidosPendenteFaturamento.map(
          (item) => ({
            title: item.Categoria,
            total: Number(
              item.TotalPendenteFaturadoCategoria.replaceAll(".", "").replace(
                ",",
                "."
              )
            ),
            datalabel: `R$ ${item.TotalPendenteFaturadoCategoria}`,
          })
        );

      const pendingBilling = {
        title: "Total a faturar",
        total: `R$ ${response.Result.PedidosFaturados.TotalPendeteFaturamento}`,
        categories: pendingBillingCategories,
        openUrl: hasAccessToTitles ? expiredTitlesUrl : null,
        noChart: !forCustomPeriod,
      };

      if (forCustomPeriod) return { billed, pendingBilling };

      return { pendingBilling, billed };
    } catch (error) {
      handleApiError(error, "order-sell-in-statusdashSellin=porCategoria");
      return {};
    }
  };

  const getBillDue = async (initialDate, endDate) => {
    try {
      const response = await Api.get(
        "order-sell-in-status",
        "reportApp",
        "?dashSellin=porTitulosVencer" +
          (filters?.idLoja ? `&idLoja=${filters?.idLoja}` : "") +
          (filters?.idGrupoEconomico
            ? `&idGrupoEconomico=${filters.idGrupoEconomico}`
            : "") +
          "&dtIni=" +
          initialDate +
          "&dtFim=" +
          endDate,
        true
      );

      const billDue = {
        title: "A vencer",
        total: response.Result?.Titulos[0]?.TotalTitulosAvencerFormat,
        noChart: true,
      };

      return { billDue };
    } catch (error) {
      handleApiError(error, "order-sell-in-status-porTitulosVencer");
      return {};
    }
  };

  const getOrdersInfo = async (initialDate, endDate, forCustomPeriod) => {
    try {
      const response = await Api.get(
        "order-sell-in-summary",
        "reportApp",
        "?dashSellin=porStatus" +
          (filters?.idLoja ? `&idLoja=${filters?.idLoja}` : "") +
          (filters.economicGroup
            ? `&idGrupoEconomico=${filters.economicGroup}`
            : "") +
          "&dtIni=" +
          initialDate +
          "&dtFim=" +
          endDate,
        true
      );

      const shoppingCategories = response?.Result?.Categorias?.map((item) => ({
        title: item.Categoria,
        total: Number(item.ValorTotal.replaceAll(".", "").replace(",", ".")),
        datalabel: `R$ ${item.ValorTotal}`,
      }));

      const shoppingInfo = {
        title: "Total de compras",
        total: `R$ ${response?.Result?.TotalVendas ?? "0,00"}`,
        categories: shoppingCategories ?? [],
        noChart: !shoppingCategories,
      };

      const ordersCategories = response?.Result?.Categorias?.map((item) => ({
        title: item.Categoria,
        total: item.Quantidade,
        datalabel: `${item.Quantidade}`,
      }));

      const queryString = `idGrupoEconomico=${filters?.idGrupoEconomico}`;
      const ordersUrl = ordersPageRedirectUrl + queryString;

      const ordersInfo = {
        title: "Total de pedidos",
        total: response?.Result?.TotalPedidos ?? 0,
        categories: ordersCategories ?? [],
        noChart: !ordersCategories,
        openUrl:
          filters?.idGrupoEconomico && hasAccessToOrders ? ordersUrl : null,
      };

      return { shoppingInfo, ordersInfo };
    } catch (error) {
      handleApiError(error, "order-sell-in-summary&dashSellin=porStatus");
      return {};
    }
  };

  const getTitlesInfo = async (initialDate, endDate) => {
    try {
      const response = await Api.get(
        "order-sell-in-summary",
        "reportApp",
        "?dashSellin=porTitulos" +
          (filters?.idLoja ? `&idLoja=${filters?.idLoja}` : "") +
          (filters?.idGrupoEconomico
            ? `&idGrupoEconomico=${filters?.idGrupoEconomico}`
            : "") +
          "&dtIni=" +
          initialDate +
          "&dtFim=" +
          endDate,
        true
      );

      const overdueAmount = `R$ ${response.Result.TotalTitulosVencidosFormat}`;
      const expiredTitles = `${response.Result.QuantidadeTitulosVencidos}`;

      return { overdueAmount, expiredTitles };
    } catch (error) {
      handleApiError(error, "order-sell-in-summary&dashSellin=porTitulos");
      return {};
    }
  };

  const getBestSellerProducts = async (initialDate, endDate) => {
    try {
      const response = await Api.get(
        "order-sell-in-status",
        "reportApp",
        "?dashSellin=porProduto" +
          (filters?.idLoja ? `&idLoja=${filters?.idLoja}` : "") +
          (filters?.idGrupoEconomico
            ? `&idGrupoEconomico=${filters?.idGrupoEconomico}`
            : "") +
          "&dtIni=" +
          initialDate +
          "&dtFim=" +
          endDate,
        true
      );

      const bestSellerProducts =
        response.Result.ProdutosRanking.ListProdutosRanking.map((item) => ({
          title: item.NomeProduto,
          total: item.Quantidade,
        }));

      return bestSellerProducts;
    } catch (error) {
      handleApiError(error, "order-sell-in-status&dashSellin=porProduto");
      return [];
    }
  };

  const fetchData = async () => {
    try {
      if (structureLoading.length > 0 || !filters?.idGrupoEconomico) {
        return;
      }

      setIsLoadingAll(true);

      const [monthOrders, monthBilling, monthBillDue] = await Promise.all([
        getOrdersInfo(firstDayOfCurrentMonth, lastDayOfCurrentMonth),
        getBillingInfo(firstDayOfCurrentMonth, lastDayOfCurrentMonth),
        getBillDue(firstDayOfCurrentMonth, lastDayOfCurrentMonth),
      ]);

      const [
        accumulatedOrders,
        accumulatedBilling,
        accumulatedBestSellers,
        accumulatedTitles,
      ] = await Promise.all([
        getOrdersInfo(firstDayOfCurrentYear, today, true),
        getBillingInfo(firstDayOfCurrentYear, today, true),
        getBestSellerProducts(firstDayOfCurrentYear, today),
        getTitlesInfo(firstDayOfCurrentYear, today),
      ]);

      const cleanedMonthBillingOrders = [
        ...Object.values(monthOrders || {}),
        ...Object.values(monthBilling || {}),
        ...Object.values(monthBillDue || {}),
      ].filter(Boolean);

      setMonthBillingOrders(cleanedMonthBillingOrders);

      const cleanedAccumulatedBillingOrders = [
        ...Object.values(accumulatedOrders || {}),
        ...Object.values(accumulatedBilling || {}),
      ].filter(Boolean);

      setAccumulatedBillingOrders({
        charts: cleanedAccumulatedBillingOrders,
        bestSellerProducts: accumulatedBestSellers || [],
        overdueAmount: accumulatedTitles?.overdueAmount,
        expiredTitles: accumulatedTitles?.expiredTitles,
      });

      setIsLoadingAll(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setIsLoadingAll(false);
    }
  };

  const handleSearchByPeriod = async (initialDate, finalDate) => {
    setSearchingByPeriod(true);

    try {
      const [
        accumulatedOrders,
        accumulatedBilling,
        accumulatedBestSellers,
        accumulatedTitles,
      ] = await Promise.all([
        getOrdersInfo(initialDate, finalDate, true),
        getBillingInfo(initialDate, finalDate, true),
        getBestSellerProducts(initialDate, finalDate),
        getTitlesInfo(initialDate, finalDate),
      ]);

      const cleanedAccumulatedBillingOrders = [
        ...Object.values(accumulatedOrders || {}),
        ...Object.values(accumulatedBilling || {}),
      ].filter(Boolean);

      setAccumulatedBillingOrders({
        charts: cleanedAccumulatedBillingOrders,
        bestSellerProducts: accumulatedBestSellers || [],
        overdueAmount: accumulatedTitles?.overdueAmount,
        expiredTitles: accumulatedTitles?.expiredTitles,
      });

      setPeriod({ initialDate, finalDate });
    } catch (error) {
      console.error("Error searching by period:", error);
    } finally {
      setSearchingByPeriod(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filters?.idLoja, filters?.idGrupoEconomico, structureLoading]);

  if (monthBillingOrders.length === 0) {
    return null;
  }

  return (
    <>
      <CardClientBillingOrders
        isAgent={true}
        title="Faturamento e pedidos/mês"
        data={{ charts: monthBillingOrders }}
      />

      <Card>
        <CardContent>
          <DateField
            label={"Período"}
            isLoading={searchingByPeriod}
            onSubmit={handleSearchByPeriod}
          />
        </CardContent>
      </Card>

      <div style={{ width: "100%" }}>
        {!searchingByPeriod && (
          <CardClientBillingOrders
            title={"Faturamento e pedidos acumulados"}
            period={period}
            data={{ ...accumulatedBillingOrders }}
          />
        )}
        <LoadingData searching={searchingByPeriod} label="Buscando dados..." />
      </div>
    </>
  );
}

export default LojistaSellIn;
